import { useEffect, useState } from "react"
import { Heading } from "@planningcenter/doxy-web"
import { useQuery } from "@tanstack/react-query"
import {
  fetchOrganization,
  fetchFunds,
  fetchText2GivePhoneNumber,
} from "../queries"
import { AlertMessage, Loading } from "source/shared/components"
import { useGivingPersonContext } from "../hooks/useGivingPersonContext"
import Connected from "./components/Connected"
import Disconnected from "./components/Disconnected"
import { APIError } from "source/shared/types"

export default function Text2Give() {
  const { givingPerson } = useGivingPersonContext()
  const [hasText2GivePhoneNumber, setHasText2GivePhoneNumber] =
    useState<boolean>(false)

  const {
    data: organization,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
  } = useQuery({
    queryKey: ["Organization"],
    queryFn: () => fetchOrganization(),
  })

  const {
    data: funds,
    isLoading: isLoadingFunds,
    isError: isErrorFunds,
  } = useQuery({
    queryKey: ["Funds"],
    queryFn: () => fetchFunds(),
  })

  type CaughtError = APIError | Error | undefined

  function codeFromError(error: CaughtError): string | undefined {
    if (error && "errors" in error && Array.isArray(error.errors)) {
      return (error as APIError).errors[0].status
    }
    return undefined
  }

  const {
    data: text2GivePhoneNumber,
    isLoading: isLoadingPhoneNumber,
    isError: isErrorPhoneNumber,
    error: phoneNumberError,
  } = useQuery({
    queryKey: ["Text2GivePhoneNumber"],
    queryFn: async () => fetchText2GivePhoneNumber(),
    enabled: !!givingPerson,
    retry: (failureCount: number, error: CaughtError) => {
      // Don't retry if error is 404, as this is the default response when no text2give phone number exists
      const statusCode = codeFromError(error)
      if (statusCode === "404") return false
      return true
    },
  })

  useEffect(() => {
    if (text2GivePhoneNumber) {
      setHasText2GivePhoneNumber(true)
    }
  }, [text2GivePhoneNumber])

  const isLoading =
    isLoadingOrganization || isLoadingFunds || isLoadingPhoneNumber
  const isError = isErrorOrganization || isErrorPhoneNumber || isErrorFunds

  return (
    <section>
      <div className="mb-2">
        <Heading level={1} size={2} text="Text2Give" />
      </div>
      {isError &&
      phoneNumberError &&
      codeFromError(phoneNumberError) !== "404" ? (
        <AlertMessage type="error" icon="general#exclamation-triangle">
          Something went wrong. Please try again.
        </AlertMessage>
      ) : isLoading ? (
        <Loading message="Loading" />
      ) : (
        organization &&
        funds &&
        (hasText2GivePhoneNumber ? (
          text2GivePhoneNumber && (
            <Connected
              organization={organization}
              funds={funds}
              text2GivePhoneNumber={text2GivePhoneNumber}
              onText2GivePhoneNumberDestroy={() =>
                setHasText2GivePhoneNumber(false)
              }
            />
          )
        ) : (
          <Disconnected organization={organization} />
        ))
      )}
    </section>
  )
}
